import React from 'react';
import { Tooltip } from 'antd';
import { CloseOutlined, WarningFilled, BulbFilled } from '@ant-design/icons';

// Chip compacto que representa un hallazgo en contextos de seleccion/drawer.
// Fase 2 del roadmap. Usa colores del whitTheme (industrial-precision):
// borde segun severidad, fondo neutro soft, icono por tipo.

const SEVERIDAD_COLOR = {
  BAJA: { border: '#2e6b3a', bg: '#e4f0e7', label: 'Baja' },
  MEDIA: { border: '#1b4f72', bg: '#e9eef7', label: 'Media' },
  ALTA: { border: '#c5561d', bg: '#faeadb', label: 'Alta' },
  CRITICA: { border: '#9b2a1e', bg: '#f5d8d3', label: 'Crítica' },
};

function HallazgoChip({ hallazgo, onRemove, onClick, showSeveridad = true, compact = false }) {
  if (!hallazgo) return null;

  const sev = hallazgo.severidad || 'MEDIA';
  const meta = SEVERIDAD_COLOR[sev] || SEVERIDAD_COLOR.MEDIA;
  const TipoIcon = hallazgo.tipo === 'INCIDENCIA' ? WarningFilled : BulbFilled;
  const ascensorLabel = hallazgo.Ascensor?.nombre || hallazgo.ascensor_nombre;
  const pulseCritical = sev === 'CRITICA';

  return (
    <div
      role={onClick ? 'button' : undefined}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: compact ? '4px 8px' : '6px 10px',
        borderRadius: 8,
        background: meta.bg,
        border: `1px solid ${meta.border}`,
        borderLeftWidth: 4,
        cursor: onClick ? 'pointer' : 'default',
        maxWidth: '100%',
        fontSize: 13,
        transition: 'box-shadow 0.16s, transform 0.16s cubic-bezier(0.22, 0.8, 0.32, 1)',
        animation: pulseCritical ? 'hallazgoChipPulse 2.4s ease-in-out infinite' : undefined,
      }}
      onMouseEnter={(e) => {
        if (onClick) e.currentTarget.style.boxShadow = '0 2px 8px -2px rgba(15,23,42,0.08)';
      }}
      onMouseLeave={(e) => {
        if (onClick) e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <TipoIcon
        style={{
          color: meta.border,
          fontSize: 13,
          flexShrink: 0,
        }}
      />
      {showSeveridad ? (
        <span
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: 0.6,
            color: meta.border,
            padding: '1px 5px',
            borderRadius: 3,
            background: 'rgba(255,255,255,0.65)',
            flexShrink: 0,
          }}
        >
          {meta.label.toUpperCase()}
        </span>
      ) : null}
      <Tooltip title={hallazgo.descripcion} mouseEnterDelay={0.4}>
        <span
          style={{
            color: '#0f172a',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: compact ? 180 : 280,
          }}
        >
          {hallazgo.descripcion}
        </span>
      </Tooltip>
      {ascensorLabel ? (
        <span
          style={{
            color: '#64748b',
            fontSize: 11,
            fontFamily: "'IBM Plex Mono', monospace",
            borderLeft: `1px solid ${meta.border}33`,
            paddingLeft: 8,
            flexShrink: 0,
          }}
        >
          {ascensorLabel}
        </span>
      ) : null}
      {onRemove ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(hallazgo.id);
          }}
          aria-label="Quitar"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 18,
            height: 18,
            border: 'none',
            background: 'transparent',
            color: '#64748b',
            cursor: 'pointer',
            padding: 0,
            borderRadius: 3,
            flexShrink: 0,
            transition: 'background 0.08s, color 0.08s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(15,23,42,0.08)';
            e.currentTarget.style.color = '#0f172a';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = '#64748b';
          }}
        >
          <CloseOutlined style={{ fontSize: 11 }} />
        </button>
      ) : null}
    </div>
  );
}

// Inyecta keyframes una sola vez. Uso idempotente — si ya existe, no hace nada.
if (typeof document !== 'undefined' && !document.getElementById('hallazgo-chip-keyframes')) {
  const style = document.createElement('style');
  style.id = 'hallazgo-chip-keyframes';
  style.textContent = `
    @keyframes hallazgoChipPulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(155, 42, 30, 0.0); }
      50% { box-shadow: 0 0 0 4px rgba(155, 42, 30, 0.14); }
    }
  `;
  document.head.appendChild(style);
}

export default HallazgoChip;
